/**
 * Live ownership registry for COM-804 "uncertain add" failures.
 *
 * A tile add that times out has an UNKNOWN outcome; `useBasketAddReconcile`
 * (mounted in AddToCart) owns that UX with its own "Confirming" toast, so the
 * watcher suppresses its failure toast. The `uncertainAddOwner=tile` form
 * sentinel only says who WOULD own it — if that owner unmounted while the add
 * was in flight (route change, Quick View closed) nobody would tell the
 * shopper anything. The hook therefore claims the product id here while it is
 * armed/active and releases on every terminal path and on unmount; the watcher
 * suppresses only when the sentinel is present AND the claim is live.
 */
const claims = new Map<string, number>();

/** Claim `productId`; returns an idempotent release function. */
export function claimUncertainAdd(productId: string): () => void {
  claims.set(productId, (claims.get(productId) ?? 0) + 1);
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const next = (claims.get(productId) ?? 1) - 1;
    if (next <= 0) claims.delete(productId);
    else claims.set(productId, next);
  };
}

export function isUncertainAddClaimed(productId: unknown): boolean {
  return typeof productId === "string" && (claims.get(productId) ?? 0) > 0;
}
